import { useCallback, useMemo } from 'react';

import { useShopProvider } from '#provider/useShopProvider.js';
import { useShopRoutes } from '#routes/useShopRoutes';
import { useCreateCart, useCreateConsumerCheckout } from '@givebacks/api/store';
import { useCheckoutTranslations, useFormListValidation } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, notifications } from 'ui';

import { SharedCheckoutButtonsProps } from '../CheckoutButtons/CheckoutButtons.types';
import { useCheckoutButtons } from '../CheckoutButtons/useCheckoutButtons';

type BuyNowButtonClick = (handleCartUpdate: () => void) => void;
interface BuyNowButtonProps extends SharedCheckoutButtonsProps {
  onBuyNowClick?: BuyNowButtonClick;
  buttonProps?: {
    children?: React.ReactNode;
  };
}

const BuyNowButton = ({
  storeId,
  storeItemId,
  cartItems,
  quantity,
  buttonProps,
  onOptions,
  onCartCreate,
  validateOnClick,
  cartFields = {},
  onBuyNowClick,
}: BuyNowButtonProps) => {
  const { navigateToCheckout } = useShopRoutes();
  const { provider } = useShopProvider();
  const { mutate: createCart, isPending: isCreateLoading } = useCreateCart();
  // Create a checkout object
  const { mutate: createConsumerCheckout, isPending: isCreateConsumerCheckoutLoading } =
    useCreateConsumerCheckout();

  const { titleCase: buyAction, isLoading: isLoadingTranslations } = useCheckoutTranslations(
    storeItemId,
    storeId
  );

  const buttonText = useMemo(() => {
    return `${buyAction} Now`;
  }, [buyAction]);

  const { hasOptions } = useCheckoutButtons({ storeId, storeItemId });

  const forms = useFormListValidation();

  const createCheckoutAndNavigateToCheckout = useCallback(
    (cartId: string) => {
      createConsumerCheckout(
        {
          checkout: {
            pending_cart_ids: [cartId],
          },
        },
        {
          onSuccess: (response) => {
            navigateToCheckout(response.checkout.uuid);
            onCartCreate();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error, 'Error creating checkout'),
            });
          },
        }
      );
    },
    [createConsumerCheckout, navigateToCheckout, onCartCreate]
  );

  const handleCartUpdate = useCallback(() => {
    if (hasOptions && onOptions) {
      return onOptions?.({ quantity, checkoutCartAction: 'buy', cartFields, cartItems });
    }

    if (validateOnClick) {
      const { isValid } = forms.validateForms();

      if (!isValid) return;
    }

    createCart(
      { storeItemId, causeId: storeId, cart_items: cartItems, provider, ...cartFields },
      {
        onSuccess(cart) {
          createCheckoutAndNavigateToCheckout(cart.uuid);
        },
        onError(e) {
          notifications.show({
            message: getErrorMessage_v2(e, 'Error adding item to cart'),
            variant: 'error',
          });
        },
      }
    );
  }, [
    createCart,
    createCheckoutAndNavigateToCheckout,
    hasOptions,
    onOptions,
    quantity,
    validateOnClick,
    forms,
    cartItems,
    storeId,
    storeItemId,
    provider,
    cartFields,
  ]);

  const handleBuyNowClick = useCallback(() => {
    if (onBuyNowClick) {
      onBuyNowClick(handleCartUpdate);
      return;
    } else {
      handleCartUpdate();
    }
  }, [handleCartUpdate, onBuyNowClick]);

  /** If any mutation is currently pending or the translations are loading, show the loading state */
  const isLoadingOrMutationPending = useMemo(() => {
    return isCreateLoading || isLoadingTranslations || isCreateConsumerCheckoutLoading;
  }, [isCreateLoading, isLoadingTranslations, isCreateConsumerCheckoutLoading]);

  return (
    <ButtonMantine onClick={handleBuyNowClick} color="cobalt" loading={isLoadingOrMutationPending}>
      {buttonProps?.children ?? buttonText}
    </ButtonMantine>
  );
};

type BuyNowButtonWrapperProps = Omit<BuyNowButtonProps, 'storeItemId' | 'storeId'> & {
  storeId?: string;
  storeItemId?: string;
};

const BuyNowButtonWrapper = ({ storeId, storeItemId, ...props }: BuyNowButtonWrapperProps) => {
  const { isBuyNowAvailable, unavailabilityReasons } = useShopProvider();

  if (!storeId || !storeItemId) return null;

  return (
    <BuyNowButton
      storeId={storeId}
      storeItemId={storeItemId}
      {...props}
      onBuyNowClick={
        isBuyNowAvailable
          ? props.onBuyNowClick
          : () => {
              notifications.show({
                message: unavailabilityReasons?.buyNow ?? 'Buy Now is not available.',
              });
            }
      }
    />
  );
};
export { BuyNowButtonWrapper as BuyNowButton };
export type { BuyNowButtonProps };
